import { useState } from 'react';
import Navbar from '../../../pages/home/components/Navbar';
import Footer from '../../../pages/home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function ClinicOwnersDirectorsPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    practiceName: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('https://readdy.ai/api/form/d62jacgr3m6fkobknu10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', practiceName: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-[#F5F3FF] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-[#7C3AED]/10 rounded-full mb-6">
                <span className="text-[#7C3AED] font-semibold text-sm">Healthcare Business Growth</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Scale Your <span className="bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent">Healthcare Business</span> with Predictable Growth
              </h1>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                HIPAA-compliant digital marketing and automated patient acquisition systems for clinic owners, medical directors, and healthcare entrepreneurs seeking sustainable, scalable growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact-form" className="px-8 py-4 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white font-semibold rounded-lg hover:shadow-xl transition-all whitespace-nowrap">
                  Get Your Growth Plan
                </a>
                <a href="#solutions" className="px-8 py-4 bg-white text-[#7C3AED] font-semibold rounded-lg border-2 border-[#7C3AED] hover:bg-[#7C3AED]/5 transition-all whitespace-nowrap">
                  See How We Help
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/c764510cdc0192d24d7d70062936525e.jpg"
                alt="Healthcare Business Office"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Growth Challenges for Healthcare Business Leaders
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the unique obstacles clinic owners and medical directors face
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-line-chart-line',
                title: 'Achieving Predictable Growth',
                description: 'Building consistent patient acquisition systems that deliver reliable, scalable results'
              },
              {
                icon: 'ri-funds-line',
                title: 'Maximizing ROI on Marketing',
                description: 'Ensuring every marketing dollar generates measurable patient acquisition and revenue'
              },
              {
                icon: 'ri-team-line',
                title: 'Managing Multiple Providers',
                description: 'Keeping all providers\' schedules full while maintaining quality patient care'
              },
              {
                icon: 'ri-time-line',
                title: 'Reducing Time on Marketing',
                description: 'Automating patient acquisition so you can focus on operations and patient care'
              },
              {
                icon: 'ri-shield-cross-line',
                title: 'HIPAA-Compliant Systems',
                description: 'Implementing sophisticated marketing technology while maintaining regulatory compliance'
              },
              {
                icon: 'ri-building-line',
                title: 'Scaling Multiple Locations',
                description: 'Growing your healthcare business across multiple clinics or service lines'
              }
            ].map((challenge, index) => (
              <div key={index} className="p-8 bg-gray-50 rounded-xl hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${challenge.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{challenge.title}</h3>
                <p className="text-gray-600 leading-relaxed">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-20 px-6 bg-gradient-to-br from-[#F5F3FF] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Marketing Automation Platform Delivers Predictable Growth
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We help healthcare business leaders achieve sustainable growth through HIPAA-compliant digital marketing and automated systems that consistently fill your practice with qualified patients while you focus on operations and patient care.
              </p>
              <div className="space-y-6">
                {[
                  'Automated patient acquisition systems that deliver consistent results',
                  'Multi-location marketing management from a single platform',
                  'Real-time analytics and ROI tracking for data-driven decisions',
                  'CRM built for healthcare business operations and growth',
                  'No missed opportunities - every lead is automatically captured and nurtured',
                  'HIPAA-conscious implementation with enterprise-grade security'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=healthcare%20business%20analytics%20dashboard%20medical%20director%20reviewing%20growth%20metrics%20clinic%20performance%20data%20modern%20office%20technology%20professional%20healthcare%20management%20strategic%20planning%20session&width=600&height=700&seq=clinic-analytics&orientation=portrait"
                alt="Healthcare Business Analytics"
                className="rounded-2xl shadow-2xl w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Healthcare Marketing Services for Business Growth
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enterprise-level digital marketing solutions for healthcare business leaders
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Medical Website Design', icon: 'ri-layout-line', link: '/services/medical-website-design' },
              { title: 'Medical SEO & Local SEO', icon: 'ri-search-line', link: '/services/medical-seo' },
              { title: 'Healthcare PPC Advertising', icon: 'ri-advertisement-line', link: '/services/healthcare-ppc' },
              { title: 'Marketing Automation', icon: 'ri-robot-line', link: '/services/marketing-automation' },
              { title: 'Analytics & Reporting', icon: 'ri-bar-chart-line', link: '/services/analytics-reporting' },
              { title: 'AI Lead Capture', icon: 'ri-customer-service-line', link: '/services/ai-lead-capture' }
            ].map((service, index) => (
              <a 
                key={index}
                href={service.link}
                className="p-8 bg-gray-50 rounded-xl hover:shadow-xl transition-all group cursor-pointer"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-[#7C3AED] font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#F5F3FF] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Healthcare Business Leaders
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Michael Chen',
                title: 'Multi-Location Clinic Owner',
                image: 'https://readdy.ai/api/search-image?query=professional%20male%20asian%20healthcare%20business%20owner%20confident%20executive%20suit%20successful%20clinic%20owner%20medical%20entrepreneur&width=150&height=150&seq=owner-test-1&orientation=squarish',
                text: 'MillionDollarMed gave us the predictable growth system we needed. We scaled from 2 to 5 locations in 18 months with consistent patient flow across all clinics.'
              },
              {
                name: 'Dr. Sarah Williams',
                title: 'Medical Director & Practice Owner',
                image: 'https://readdy.ai/api/search-image?query=professional%20female%20doctor%20medical%20director%20confident%20business%20attire%20healthcare%20executive%20successful%20practice%20owner&width=150&height=150&seq=owner-test-2&orientation=squarish',
                text: 'The automation platform freed up 15+ hours per week that I was spending on marketing. Now I focus on patient care and business strategy while new patients come in automatically.'
              },
              {
                name: 'James Rodriguez',
                title: 'Healthcare Entrepreneur',
                image: 'https://readdy.ai/api/search-image?query=professional%20male%20hispanic%20healthcare%20entrepreneur%20confident%20executive%20modern%20office%20successful%20business%20leader&width=150&height=150&seq=owner-test-3&orientation=squarish',
                text: 'Their HIPAA-compliant marketing systems and real-time analytics transformed our business. We achieved 340% ROI in the first year with complete visibility into every marketing dollar.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="p-8 bg-white rounded-xl shadow-lg">
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover object-top"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.title}</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Book Your Free Strategy Call
            </h2>
            <p className="text-xl text-gray-600">
              Let's discuss how we can help you achieve predictable, scalable growth
            </p>
          </div>
          <form onSubmit={handleSubmit} className="bg-gray-50 p-8 rounded-2xl" data-readdy-form id="clinic-owners-directors-strategy">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Business/Practice Name *</label>
                <input
                  type="text"
                  name="practiceName"
                  value={formData.practiceName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tell Us About Your Business Goals</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent text-sm resize-none"
                placeholder="What are your main growth challenges?"
              ></textarea>
              <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
            </div>
            <button
              type="submit"
              className="w-full px-8 py-4 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white font-semibold rounded-lg hover:shadow-xl transition-all whitespace-nowrap"
            >
              Book My Strategy Call
            </button>
            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium">Thank you! We'll contact you within 24 hours to schedule your strategy call.</p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium">Something went wrong. Please try again or email us directly.</p>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-4 text-center">
              Your information is protected and will be handled in accordance with HIPAA guidelines.
            </p>
          </form>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-r from-[#0D9488] to-[#14B8A6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Scale Your Healthcare Business?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Let's build a HIPAA-compliant marketing system that delivers predictable growth
          </p>
          <a href="#contact-form" className="inline-block px-8 py-4 bg-white text-[#0D9488] font-semibold rounded-lg hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer">
            Schedule Your Free Consultation
          </a>
        </div>
      </section>

      <RelatedServices currentSlug="clinic-owners-directors" />
      <RelatedIndustries currentSlug="clinic-owners-directors" />

      <Footer />
    </div>
  );
}